import React, { useState } from "react";
import { useFormContext } from "react-hook-form";
import { OrganizationConfiguration } from "../../models/OrganizationConfiguration/OrganizationConfiguration";
import OrganizationsRepository from "../../repositories/OrganizationsRepository";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "@/components/ui/table";
import { Trash2 } from "lucide-react";
import { toast } from "sonner";
import ConfirmationModal from "../common/ConfirmationModal";

interface MicrosoftApiAuthConfigurationSectionProps {
  disabled?: boolean;
}

const MicrosoftApiAuthConfigurationSection: React.FC<MicrosoftApiAuthConfigurationSectionProps> = ({
  disabled = false,
}) => {
  const form = useFormContext<OrganizationConfiguration>();
  const organizationsRepository = new OrganizationsRepository();
  const [deleteRefreshTokenId, setDeleteRefreshTokenId] = useState<string | null>(null);
  const [deleteRefreshTokenUserPrincipalName, setDeleteRefreshTokenUserPrincipalName] = useState<string | null>(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);

  const microsoftRefreshTokens = form.watch("microsoftRefreshTokens");

  const handleDeleteMicrosoftRefreshToken = (id: string, upn: string) => {
    setDeleteRefreshTokenId(id);
    setDeleteRefreshTokenUserPrincipalName(upn);
    setIsDeleteModalOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (deleteRefreshTokenId) {
      try {
        await organizationsRepository.deleteMicrosoftRefreshToken(deleteRefreshTokenId);
        form.setValue("microsoftRefreshTokens",
          microsoftRefreshTokens?.filter(
            (token) => token.id !== deleteRefreshTokenId
          ) ?? null
        );
      } catch {
        toast.error("Failed to delete the Microsoft refresh token");
      } finally {
        handleCloseDeleteModal();
      }
    }
  };

  const handleCloseDeleteModal = () => {
    setIsDeleteModalOpen(false);
    setDeleteRefreshTokenId(null);
    setDeleteRefreshTokenUserPrincipalName(null);
  };

  return (
    <>
      {microsoftRefreshTokens == null ||
      microsoftRefreshTokens.length === 0 ? (
        <span className="text-sm italic">
          No Microsoft refresh tokens configured. Use the "Authorize" button to
          add one.
        </span>
      ) : (
        <></>
      )}

      <Card
        className={
          microsoftRefreshTokens != null && microsoftRefreshTokens.length > 0
            ? "p-0 gap-0 flex flex-col flex-1 min-h-0"
            : "hidden"
        }
      >
        <Table containerClassName="flex-1 min-h-0">
          <TableHeader>
            <TableRow>
              <TableHead className="w-[10px] px-1.5 py-3"></TableHead>
              <TableHead className="px-1.5 py-3">User Principal Name</TableHead>
              <TableHead className="w-[60px] px-1.5 py-3"></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {microsoftRefreshTokens != null &&
              microsoftRefreshTokens.map((microsoftRefreshToken) => (
                <TableRow key={microsoftRefreshToken.userPrincipalName}>
                  <TableCell />
                  <TableCell>
                    <span className="text-sm">
                      {microsoftRefreshToken.userPrincipalName}
                    </span>
                  </TableCell>
                  <TableCell className="text-center">
                    <Button
                      type="button"
                      disabled={disabled}
                      aria-label="Delete"
                      variant="ghost"
                      size="icon-sm"
                      className="text-destructive hover:text-destructive"
                      onClick={() =>
                        handleDeleteMicrosoftRefreshToken(microsoftRefreshToken.id, microsoftRefreshToken.userPrincipalName)
                      }
                    >
                      <Trash2 className="size-4" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
          </TableBody>
        </Table>
      </Card>

      <ConfirmationModal
        open={isDeleteModalOpen}
        onClose={handleCloseDeleteModal}
        onConfirm={handleConfirmDelete}
        message="Are you sure you want to delete this refresh token?"
        statusColor="danger"
        buttonText="Delete"
      >
        <div className="flex flex-wrap">
          <Badge variant="outline-primary">{deleteRefreshTokenUserPrincipalName || ""}</Badge>
        </div>
      </ConfirmationModal>
    </>
  );
};

export default MicrosoftApiAuthConfigurationSection;
